import React from 'react';
import { ctaData } from '../data/mock';
import { ArrowRight } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import FloatingParticles from './graphics/FloatingParticles';

const CALENDLY_LINK = 'https://calendly.com/shantanu-leadership-coaching/1-1-coffee-chat?month=2026-03';

export const CTASection = () => {
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="contact" className="bg-gradient-to-b from-[#0f1a2a] to-[#0a1420] py-24 lg:py-32 relative overflow-hidden">
      {/* Background elements */}
      <div className="noise-overlay" />
      <FloatingParticles count={12} />
      
      {/* Central glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#5a7fa8] opacity-[0.04] blur-[100px] rounded-full" />
      
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-48 h-48">
        <div className="absolute top-12 left-12 w-px h-24 bg-gradient-to-b from-[#5a7fa8]/30 to-transparent" />
        <div className="absolute top-12 left-12 w-24 h-px bg-gradient-to-r from-[#5a7fa8]/30 to-transparent" />
        <div className="absolute top-11 left-11 w-2 h-2 rounded-full bg-[#5a7fa8]/40" />
      </div>
      <div className="absolute bottom-0 right-0 w-48 h-48">
        <div className="absolute bottom-12 right-12 w-px h-24 bg-gradient-to-t from-[#c9a87c]/30 to-transparent" />
        <div className="absolute bottom-12 right-12 w-24 h-px bg-gradient-to-l from-[#c9a87c]/30 to-transparent" />
        <div className="absolute bottom-11 right-11 w-2 h-2 rounded-full bg-[#c9a87c]/40" />
      </div>
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        <div 
          ref={contentRef}
          className={`max-w-2xl mx-auto text-center animate-on-scroll ${contentVisible ? 'is-visible' : ''}`}
        >
          {/* Decorative element */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-8 h-px bg-gradient-to-r from-transparent to-[#5a7fa8]" />
            <div className="w-2 h-2 rounded-full bg-[#c9a87c] breathe" />
            <div className="w-8 h-px bg-gradient-to-l from-transparent to-[#5a7fa8]" />
          </div>
          
          {/* Headline */}
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#f5f5f0] leading-tight mb-6">
            {ctaData.headline}
          </h2>

          {/* Description */}
          <p className="text-base text-[#6b7c93] leading-relaxed mb-12">
            {ctaData.description}
          </p>

          {/* CTA Button */}
          <div className="flex justify-center">
            <a 
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center justify-center gap-3 px-10 py-5 text-sm font-medium overflow-hidden"
            >
              {/* Background layers */}
              <span className="absolute inset-0 bg-[#f5f5f0]" />
              <span className="absolute inset-0 bg-gradient-to-r from-[#c9a87c] via-[#5a7fa8] to-[#c9a87c] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Content */}
              <span className="relative z-10 text-[#0f1a2a] group-hover:text-white transition-colors duration-500">{ctaData.primaryCta}</span>
              <ArrowRight size={16} className="relative z-10 text-[#0f1a2a] group-hover:text-white transition-all duration-500 group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
